<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import { api } from "@/services/api";
import type { Task, TaskDetail } from "@/types";
import TaskDrawer from "@/components/TaskDrawer.vue";
import { useBriefStore } from "@/stores/briefs";

type DashboardTask = Task & { brief_title: string };
const dashboard = ref<{
  counts: {
    total: number;
    in_progress: number;
    ready_for_acceptance: number;
    accepted: number;
  };
  tasks: DashboardTask[];
}>({
  counts: { total: 0, in_progress: 0, ready_for_acceptance: 0, accepted: 0 },
  tasks: [],
});
const selectedTask = ref<TaskDetail | null>(null);
const briefStore = useBriefStore();
const loading = ref(false);
let refreshTimer: number | undefined;
const cards = computed(() => [
  { label: "全部任务", value: dashboard.value.counts.total, tone: "neutral" },
  { label: "进行中", value: dashboard.value.counts.in_progress, tone: "blue" },
  {
    label: "等待我反馈",
    value: dashboard.value.counts.ready_for_acceptance,
    tone: "orange",
  },
  { label: "已完成", value: dashboard.value.counts.accepted, tone: "green" },
]);
const statusText = (status: string) =>
  ({
    submitted: "团队已接收",
    in_progress: "进行中",
    ready_for_acceptance: "等待你的反馈",
    accepted: "已完成",
    revision_requested: "修改中",
  })[status] || "待确认";
async function load() {
  loading.value = true;
  try {
    dashboard.value = await api("/client/dashboard");
  } finally {
    loading.value = false;
  }
}
async function openTask(id: number) {
  selectedTask.value = (await api<{ task: TaskDetail }>(`/tasks/${id}`)).task;
}
onMounted(() => {
  void Promise.all([load(), briefStore.loadList()]);
  refreshTimer = window.setInterval(() => {
    void load();
    void briefStore.loadList();
    if (selectedTask.value) void openTask(selectedTask.value.id);
  }, 8000);
});
onUnmounted(() => {
  if (refreshTimer) window.clearInterval(refreshTimer);
});
</script>

<template>
  <div class="dashboard">
    <header class="page-head">
      <div>
        <h1>工作台</h1>
        <p class="muted">查看已提交任务的进度、素材、交付与反馈。</p>
      </div>
    </header>
    <section class="metrics">
      <article
        v-for="card in cards"
        :key="card.label"
        class="metric"
        :class="`metric--${card.tone}`"
      >
        <span>{{ card.label }}</span
        ><b>{{ card.value }}</b>
      </article>
    </section>
    <section class="task-section">
      <header>
        <h2>任务跟踪</h2>
        <span class="muted">{{
          loading ? "加载中..." : `${dashboard.tasks.length} 项任务`
        }}</span>
      </header>
      <button
        v-for="task in dashboard.tasks"
        :key="task.id"
        class="task-row"
        @click="openTask(task.id)"
      >
        <span class="task-row__main"
          ><b>{{ task.client_title || task.title }}</b
          ><small>{{ task.brief_title }}</small></span
        ><span class="status">{{ statusText(task.status) }}</span
        ><time v-if="task.expected_at">预计 {{ task.expected_at }}</time>
      </button>
      <p v-if="!loading && !dashboard.tasks.length" class="empty muted">
        暂时还没有已确认的任务。
      </p>
    </section>
    <TaskDrawer
      :task="selectedTask"
      @close="selectedTask = null"
      @changed="
        selectedTask && openTask(selectedTask.id);
        load();
      "
    />
  </div>
</template>

<style scoped>
.dashboard {
  min-height: calc(100vh - 64px);
  background: #f7f9f9;
}
.page-head {
  padding: 28px 30px 20px;
  background: #fff;
  border-bottom: 1px solid var(--line);
}
.page-head h1,
.task-section h2 {
  margin: 0;
}
.page-head p {
  margin: 6px 0 0;
}
.metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  padding: 22px 30px;
}
.metric {
  display: flex;
  min-height: 104px;
  flex-direction: column;
  justify-content: space-between;
  padding: 18px;
  border: 1px solid var(--line);
  border-top: 3px solid #aeb7b8;
  background: #fff;
}
.metric b {
  font-size: 30px;
}
.metric--blue {
  border-top-color: #2786d7;
}
.metric--orange {
  border-top-color: #ee8b32;
}
.metric--green {
  border-top-color: #20a678;
}
.task-section {
  margin: 0 30px;
  padding: 18px;
  background: #fff;
  border: 1px solid var(--line);
}
.task-section header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 0 14px;
  border-bottom: 1px solid var(--line);
}
.task-section h2 {
  font-size: 17px;
}
.task-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 18px;
  width: 100%;
  padding: 16px 0;
  border: 0;
  border-bottom: 1px solid var(--line);
  background: transparent;
  text-align: left;
}
.task-row:hover b {
  color: var(--brand-dark);
}
.task-row__main b,
.task-row__main small {
  display: block;
}
.task-row__main small,
time {
  margin-top: 5px;
  color: var(--muted);
  font-size: 12px;
}
.empty {
  padding: 28px;
  text-align: center;
}
@media (max-width: 700px) {
  .page-head {
    padding: 20px 14px;
  }
  .metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 9px;
    padding: 14px;
  }
  .metric {
    min-height: 88px;
    padding: 14px;
  }
  .task-section {
    margin: 0 14px;
    padding: 14px;
  }
  .task-row {
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 9px;
  }
  .task-row time {
    grid-column: 1 / -1;
    margin: 0;
  }
}
</style>
